import React, { useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
  Chip,
  Box,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import { getPicklistValues } from "../config/picklistConfig";

interface PicklistOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectPicklistProps {
  fieldName: string;
  value: string; // semicolon-separated
  onChange: (e: { target: { name: string; value: string[] } }) => void;
  label?: string;
  objectName?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  options?: PicklistOption[];
  maxSelections?: number;
  exclusiveOptions?: string[];
}

const MultiSelectPicklist: React.FC<MultiSelectPicklistProps> = ({
  fieldName,
  value = "",
  onChange,
  label,
  objectName = "caregiver",
  required = false,
  error = false,
  helperText = "",
  options,
  maxSelections,
  exclusiveOptions = [],
}) => {
  const { i18n } = useTranslation();

  const picklistOptions: PicklistOption[] = useMemo(() => {
    if (options) return options;

    const engValues = getPicklistValues(objectName, fieldName);

    let translatedValues: string[] = [];
    if (i18n.language !== "en") {
      translatedValues =
        getPicklistValues(objectName, `${fieldName}${i18n.language}`) || [];
    }

    return engValues.map((engValue: string, index: number) => ({
      value: engValue,
      label:
        i18n.language !== "en" && translatedValues[index]
          ? translatedValues[index]
          : engValue,
    }));
  }, [objectName, fieldName, i18n.language, options]);

  const selectedValues: string[] = Array.isArray(value)
    ? value.filter(Boolean)
    : value
      ? value.split(";").filter(Boolean)
      : [];

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    let newSelectedValues = event.target.value as string[];

    if (exclusiveOptions.length > 0) {
      const justAdded = newSelectedValues.filter((v) => !selectedValues.includes(v));
      const addedExclusive = justAdded.find((v) => exclusiveOptions.includes(v));

      if (addedExclusive) {
        // Exclusive option was just selected -- deselect everything else
        newSelectedValues = [addedExclusive];
      } else if (justAdded.length > 0) {
        // A non-exclusive option was selected -- remove any exclusive options
        newSelectedValues = newSelectedValues.filter((v) => !exclusiveOptions.includes(v));
      }
    }

    onChange({
      target: {
        name: fieldName,
        value: newSelectedValues,
      },
    });
  };

  const displayLabel =
    label ||
    fieldName
      .replace("__c", "")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .trim();

  return (
    <FormControl fullWidth error={error} required={required}>
      <InputLabel id={`${fieldName}-label`}>{displayLabel}</InputLabel>
      <Select
        labelId={`${fieldName}-label`}
        multiple
        name={fieldName}
        value={selectedValues}
        onChange={handleChange}
        input={<OutlinedInput label={displayLabel} />}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(selected as string[]).map((engVal) => {
              const matchedOption = picklistOptions.find(
                (opt) => opt.value === engVal
              );
              const chipLabel = matchedOption ? matchedOption.label : engVal;
              return <Chip key={engVal} label={chipLabel} />;
            })}
          </Box>
        )}
      >
        {picklistOptions.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const isDisabled =
            (maxSelections !== undefined &&
              selectedValues.length >= maxSelections &&
              !isSelected) ||
            option.disabled === true;

          return (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={isDisabled}
            >
              <Checkbox checked={isSelected} />
              <ListItemText primary={option.label} />
            </MenuItem>
          );
        })}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default MultiSelectPicklist;
